import { closeX } from './popup.js';
import { $, clamp, el } from '../state/store.js';

/* CROP — freeform drag-crop for reference photos, shown right after a photo
   is chosen (both the initial upload and Replace go through this), before
   corner marking starts. Same .authwrap/.authcard overlay convention as
   settings.js/billing.js. The crop rect starts covering the whole photo, so
   clicking Apply crop without touching anything is how you skip cropping —
   no separate "use full photo" control needed. */

const MIN = 0.03;   // smallest crop rect edge, normalised to the image
// How many screen px of drag equal one degree on the straighten ruler —
// also how far the ruler's own tick pattern scrolls per px of drag, so the
// ticks track the pointer 1:1 regardless of the angle clamp below.
const PX_PER_DEG = 6;
const MAX_STRAIGHTEN = 45;

let overlay = null, stage = null, imgClip = null, img = null, rectEl = null, selOverlay = null, gridEl = null;
let straightenTrack = null, straightenLabel = null;
const shades = {}, handles = {};
let rect = { x0: 0, y0: 0, x1: 1, y1: 1 };
let naturalW = 0, naturalH = 0, origUrl = null;
let resolveFn = null;
// Whether the working image has been rotated/straightened away from what
// origUrl decodes to — finish()'s "untouched crop" fast path hands back
// origUrl as-is to skip a lossy re-encode, which would silently discard
// any rotation, so that shortcut is only safe to take while this is false.
let rotated = false;
let straightenDrag = null;
/* Which cropPhoto call is the live one. The dialog now opens BEFORE its picture
   exists, so a download can still be in flight when the person cancels or asks
   for a different photo — and the late arrival must not paint itself into a
   dialog that has moved on. */
let gen = 0;

/* The state the dialog opens in: on screen, and waiting. Everything here acts
   on an image that is not there yet, so none of it can be offered — Apply would
   hand back a crop of nothing, and rotate and straighten would bake a rotation
   into an empty canvas. */
function setBusy(on) {
  overlay.classList.toggle('cropwrap--busy', on);
  /* The instruction is for a photo that is on screen; while it is still coming,
     saying what is happening is the more useful sentence. */
  const n = $('#cropNote');
  if (n) n.textContent = on
    ? 'Loading the photo…'
    : 'Drag a corner to resize, drag inside to move. The full photo is used if you leave it as-is.';
  const a = $('#cropApply'); if (a) a.disabled = on;
  overlay.querySelectorAll('.cropRotateRow button').forEach(b => { b.disabled = on; });
  /* Cleared so the placeholder box in CSS can take over; fitStage sets them
     again from the photo's own proportions the moment it decodes. */
  if (on && stage) { stage.style.width = ''; stage.style.height = ''; }
}

function ensure() {
  if (overlay) return;
  overlay = el('div', 'authwrap hidden');
  overlay.id = 'cropwrap';
  const card = el('div', 'authcard authcard--crop');
  card.appendChild(el('h2', null, 'Crop photo'));
  closeX(card, () => finish(null));
  const note = el('p', 'authnote',
    'Drag a corner to resize, drag inside to move. The full photo is used if you leave it as-is.');
  note.id = 'cropNote';
  card.appendChild(note);

  const rotRow = el('div', 'btnrow cropRotateRow');
  const rotL = el('button', 'btn btn--sm btn--gh', '↺ Rotate left');
  const rotR = el('button', 'btn btn--sm btn--gh', '↻ Rotate right');
  rotL.type = 'button'; rotR.type = 'button';
  rotL.onclick = () => rotate90(-1);
  rotR.onclick = () => rotate90(1);
  rotRow.appendChild(rotL); rotRow.appendChild(rotR);
  card.appendChild(rotRow);

  stage = el('div', 'cropstage');

  imgClip = el('div', 'cropimgclip');
  img = document.createElement('img');
  img.draggable = false;
  imgClip.appendChild(img);
  stage.appendChild(imgClip);

  gridEl = el('div', 'cropgrid');
  stage.appendChild(gridEl);

  // Selection chrome (shading + rect + handles) lives in its own layer so
  // dragging the straighten ruler can hide all of it with one visibility
  // toggle instead of nine.
  selOverlay = el('div', 'cropsel');
  ['t', 'b', 'l', 'r'].forEach(k => { shades[k] = el('div', 'cropshade'); selOverlay.appendChild(shades[k]); });

  rectEl = el('div', 'croprect');
  rectEl.addEventListener('pointerdown', onRectDown);
  selOverlay.appendChild(rectEl);

  ['tl', 'tr', 'bl', 'br'].forEach(k => {
    const h = el('div', 'crophandle ch-' + k);
    h.addEventListener('pointerdown', ev => onHandleDown(ev, k));
    handles[k] = h;
    selOverlay.appendChild(h);
  });
  stage.appendChild(selOverlay);
  card.appendChild(stage);

  // STRAIGHTEN — a continuous-angle rotate, same idea as the iPhone Photos
  // crop tool's ruler: drag to tilt the photo by any amount, release to
  // bake it in. Unlike Rotate left/right (lossless, swaps the frame's own
  // width/height), this keeps the frame's aspect fixed and scales the
  // photo up just enough that the tilt never exposes empty corners — the
  // same trade a real straighten tool makes.
  const straightenRow = el('div', 'cropstraighten');
  straightenLabel = el('button', 'straightenLabel', '0.0°');
  straightenLabel.type = 'button';
  straightenLabel.title = 'Reset straighten';
  straightenTrack = el('div', 'straightenTrack');
  const needle = el('div', 'straightenNeedle');
  straightenTrack.appendChild(needle);
  straightenTrack.addEventListener('pointerdown', onStraightenDown);
  straightenRow.appendChild(straightenLabel);
  straightenRow.appendChild(straightenTrack);
  card.appendChild(straightenRow);

  const row = el('div', 'authrow');
  const cancel = el('button', 'btn btn--gh', 'Cancel');
  const apply = el('button', 'btn', 'Apply crop');
  cancel.id = 'cropCancel'; apply.id = 'cropApply';
  cancel.onclick = () => finish(null);
  apply.onclick = () => finish(rect);
  row.appendChild(cancel); row.appendChild(apply);
  card.appendChild(row);

  overlay.appendChild(card);
  document.body.appendChild(overlay);
}

/* Scale factor that keeps a W×H rectangle, rotated by `rad` about its own
   centre, fully covering the original (unrotated) W×H frame — i.e. how much
   bigger the photo has to be drawn so straightening never leaves an empty
   corner. Standard "rotate to cover" derivation: the frame's own corner
   (W/2,H/2) must land inside the rotated, scaled rectangle on both of its
   local axes; solving both gives one constraint per axis, and the tighter
   of the two wins. */
function coverScale(rad, W, H) {
  const c = Math.abs(Math.cos(rad)), s = Math.abs(Math.sin(rad));
  return Math.max(c + (H / W) * s, (W / H) * s + c);
}

/* Rotates the CURRENT working image (not the original) by another 90° and
   makes that the new working image — bakes into real pixels via a canvas
   redraw rather than a CSS transform, so everything downstream (fitStage,
   paint, finish's drawImage) keeps working against img's natural content
   completely unaware a rotation ever happened, no rotation-aware math
   needed anywhere else. The crop rect resets to full-image on rotate:
   transforming an existing partial rect through a 90° turn is solvable,
   but rotating first and then cropping is both the common case and the
   one that needs no such math at all. */
function rotate90(dir) {
  const cv = document.createElement('canvas');
  cv.width = naturalH; cv.height = naturalW;
  const ctx = cv.getContext('2d');
  ctx.translate(cv.width / 2, cv.height / 2);
  ctx.rotate(dir * Math.PI / 2);
  ctx.drawImage(img, -naturalW / 2, -naturalH / 2);
  naturalW = cv.width; naturalH = cv.height;
  rotated = true;
  rect = { x0: 0, y0: 0, x1: 1, y1: 1 };
  img.onload = () => { fitStage(); paint(); };
  img.src = cv.toDataURL('image/png');
}

/* Bakes a free-angle tilt (from the straighten ruler) into the working
   image. Unlike rotate90, the frame's own width/height don't change — the
   photo is scaled up by coverScale() and redrawn centred, so the corners
   that would otherwise go empty are cropped away instead, matching what
   the live preview already showed under the pointer. */
function applyStraighten(deltaDeg) {
  const rad = deltaDeg * Math.PI / 180;
  const W = naturalW, H = naturalH;
  const scale = coverScale(rad, W, H);
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');
  ctx.translate(W / 2, H / 2);
  ctx.rotate(rad);
  ctx.scale(scale, scale);
  ctx.drawImage(img, -W / 2, -H / 2);
  rotated = true;
  rect = { x0: 0, y0: 0, x1: 1, y1: 1 };
  img.style.transform = '';
  img.onload = () => { fitStage(); paint(); };
  img.src = cv.toDataURL('image/png');
}

function setSelVisible(on) {
  selOverlay.style.visibility = on ? '' : 'hidden';
}

function updateStraightenPreview(angle) {
  straightenLabel.textContent = angle.toFixed(1) + '°';
  const rad = angle * Math.PI / 180;
  const scale = coverScale(rad, naturalW, naturalH);
  img.style.transform = `rotate(${angle}deg) scale(${scale})`;
}

function onStraightenDown(ev) {
  ev.preventDefault();
  straightenTrack.setPointerCapture(ev.pointerId);
  straightenDrag = { x: ev.clientX, angle: 0 };
  gridEl.classList.add('on');
  setSelVisible(false);
  const move = e => {
    if (!straightenDrag) return;
    const dx = e.clientX - straightenDrag.x;
    straightenTrack.style.backgroundPositionX = dx + 'px';
    straightenDrag.angle = clamp(dx / PX_PER_DEG, -MAX_STRAIGHTEN, MAX_STRAIGHTEN);
    updateStraightenPreview(straightenDrag.angle);
  };
  const up = () => {
    straightenTrack.removeEventListener('pointermove', move);
    straightenTrack.removeEventListener('pointerup', up);
    straightenTrack.removeEventListener('pointercancel', up);
    const angle = straightenDrag ? straightenDrag.angle : 0;
    straightenDrag = null;
    straightenTrack.style.backgroundPositionX = '0px';
    straightenLabel.textContent = '0.0°';
    gridEl.classList.remove('on');
    setSelVisible(true);
    if (Math.abs(angle) > 0.05) applyStraighten(angle);
    else img.style.transform = '';
  };
  straightenTrack.addEventListener('pointermove', move);
  straightenTrack.addEventListener('pointerup', up);
  straightenTrack.addEventListener('pointercancel', up);
}

function fitStage() {
  const maxW = Math.min(window.innerWidth - 64, 900);
  const maxH = window.innerHeight - 300;   // room for heading, note, rotate row, straighten row and buttons
  const scale = Math.min(maxW / naturalW, maxH / naturalH, 1);
  const w = Math.round(naturalW * scale), h = Math.round(naturalH * scale);
  stage.style.width = w + 'px';
  stage.style.height = h + 'px';
  /* Published so the rotate and straighten rows can match the photo's width —
     see .cropRotateRow/.cropstraighten. Set on the card rather than the stage
     because those rows are siblings of it, not children. */
  stage.parentElement?.style.setProperty('--crop-w', w + 'px');
}

function paint() {
  const W = stage.clientWidth, H = stage.clientHeight;
  const px = { x0: rect.x0 * W, y0: rect.y0 * H, x1: rect.x1 * W, y1: rect.y1 * H };
  rectEl.style.cssText = `left:${px.x0}px;top:${px.y0}px;width:${px.x1 - px.x0}px;height:${px.y1 - px.y0}px`;
  shades.t.style.cssText = `left:0;top:0;width:100%;height:${px.y0}px`;
  shades.b.style.cssText = `left:0;top:${px.y1}px;width:100%;height:${H - px.y1}px`;
  shades.l.style.cssText = `left:0;top:${px.y0}px;width:${px.x0}px;height:${px.y1 - px.y0}px`;
  shades.r.style.cssText = `left:${px.x1}px;top:${px.y0}px;width:${W - px.x1}px;height:${px.y1 - px.y0}px`;
  handles.tl.style.cssText = `left:${px.x0}px;top:${px.y0}px`;
  handles.tr.style.cssText = `left:${px.x1}px;top:${px.y0}px`;
  handles.bl.style.cssText = `left:${px.x0}px;top:${px.y1}px`;
  handles.br.style.cssText = `left:${px.x1}px;top:${px.y1}px`;
}

function onHandleDown(ev, k) {
  ev.preventDefault(); ev.stopPropagation();
  const h = handles[k];
  h.setPointerCapture(ev.pointerId);
  const move = e => {
    const r = stage.getBoundingClientRect();
    const nx = clamp((e.clientX - r.left) / r.width, 0, 1);
    const ny = clamp((e.clientY - r.top) / r.height, 0, 1);
    if (k[0] === 't') rect.y0 = Math.min(ny, rect.y1 - MIN); else rect.y1 = Math.max(ny, rect.y0 + MIN);
    if (k[1] === 'l') rect.x0 = Math.min(nx, rect.x1 - MIN); else rect.x1 = Math.max(nx, rect.x0 + MIN);
    paint();
  };
  const up = () => {
    h.removeEventListener('pointermove', move);
    h.removeEventListener('pointerup', up);
    h.removeEventListener('pointercancel', up);
  };
  h.addEventListener('pointermove', move);
  h.addEventListener('pointerup', up);
  h.addEventListener('pointercancel', up);
}

function onRectDown(ev) {
  ev.preventDefault(); ev.stopPropagation();
  rectEl.setPointerCapture(ev.pointerId);
  const r0 = stage.getBoundingClientRect();
  const start = { x: ev.clientX, y: ev.clientY, rect: { ...rect } };
  const move = e => {
    const dx = (e.clientX - start.x) / r0.width, dy = (e.clientY - start.y) / r0.height;
    const w = start.rect.x1 - start.rect.x0, h = start.rect.y1 - start.rect.y0;
    const x0 = clamp(start.rect.x0 + dx, 0, 1 - w), y0 = clamp(start.rect.y0 + dy, 0, 1 - h);
    rect = { x0, y0, x1: x0 + w, y1: y0 + h };
    paint();
  };
  const up = () => {
    rectEl.removeEventListener('pointermove', move);
    rectEl.removeEventListener('pointerup', up);
    rectEl.removeEventListener('pointercancel', up);
  };
  rectEl.addEventListener('pointermove', move);
  rectEl.addEventListener('pointerup', up);
  rectEl.addEventListener('pointercancel', up);
}

function finish(result) {
  gen++;                                 // whatever is still loading is no longer wanted
  overlay.classList.add('hidden');
  if (!result) { resolveFn(null); return; }
  // An untouched (full-image) rect means "skip cropping" — hand back the
  // original file exactly as uploaded rather than round-tripping it through
  // a lossy JPEG re-encode for no reason. Not safe once rotated, though:
  // origUrl still decodes to the UNROTATED photo, so that shortcut would
  // silently throw the rotation away — fall through to the canvas redraw
  // below instead, which samples img (the current, rotated content).
  const untouched = !rotated && result.x0 < 1e-6 && result.y0 < 1e-6 && result.x1 > 1 - 1e-6 && result.y1 > 1 - 1e-6;
  /* The rect travels back with the result so the caller can carry already-marked
     corners across a re-crop (see core/crop-map.js). Normalised to the image
     that was handed IN, which is what makes two passes comparable — and only
     meaningful while `rotated` is false, since a rotate bakes new content into
     the working image and a rect stops describing the relationship. The flag
     goes with it rather than being inferred, so the caller cannot get that
     wrong by accident. */
  if (untouched) {
    resolveFn({ url: origUrl, w: naturalW, h: naturalH, rect: { x0: 0, y0: 0, x1: 1, y1: 1 }, rotated: false });
    return;
  }
  const sx = Math.round(result.x0 * naturalW), sy = Math.round(result.y0 * naturalH);
  const sw = Math.round((result.x1 - result.x0) * naturalW), sh = Math.round((result.y1 - result.y0) * naturalH);
  const cv = document.createElement('canvas');
  cv.width = sw; cv.height = sh;
  // img already holds the full-resolution decoded bitmap regardless of its
  // CSS-scaled display size, so drawImage samples it at full quality — no
  // second decode needed.
  cv.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  resolveFn({
    url: cv.toDataURL('image/jpeg', 0.92), w: sw, h: sh,
    rect: { x0: result.x0, y0: result.y0, x1: result.x1, y1: result.y1 }, rotated
  });
}

/**
 * @param {string} dataUrl - the freshly-loaded photo
 * @returns {Promise<{url:string,w:number,h:number}|null>} the crop, or null on Cancel
 */
/**
 * @param {string|Promise<string>} source  the photo, or the promise of one
 *
 * THE DIALOG OPENS FIRST AND THE PICTURE ARRIVES INTO IT. It used to be handed
 * a data URL, which meant every caller had to finish downloading, reading and
 * decoding the photo before anything appeared — 21ms on a desktop and three
 * seconds on a slow connection, all of it after a click, with nothing on screen
 * to say the click had registered. Taking a promise moves that wait inside the
 * dialog, where there is something to look at while it happens.
 */
export function cropPhoto(source) {
  ensure();
  const my = ++gen;
  origUrl = null;
  rect = { x0: 0, y0: 0, x1: 1, y1: 1 };
  rotated = false;
  straightenTrack.style.backgroundPositionX = '0px';
  straightenLabel.textContent = '0.0°';
  img.removeAttribute('src');            // never the previous photo, however briefly
  overlay.classList.remove('hidden');
  setBusy(true);
  return new Promise(resolve => {
    resolveFn = resolve;
    Promise.resolve(source).then(url => {
      if (my !== gen) return;            // cancelled, or replaced by another photo
      origUrl = url;
      img.src = url;
    }, () => {
      if (my !== gen) return;
      /* Closed rather than left open on an error the dialog cannot explain —
         the caller knows what it was fetching and says so. */
      overlay.classList.add('hidden');
      setBusy(false);
      resolve(null);
    });
    img.onload = () => {
      if (my !== gen) return;
      // Measured from this element's own decode, not a separate Image()
      // the caller loaded first — the two can disagree on orientation
      // (EXIF is the usual cause: some decode paths auto-rotate, some
      // don't, so one measurement can report width/height swapped
      // relative to the other) and drawImage() below always samples THIS
      // element, so the crop math has to agree with it specifically or
      // the output comes out stretched — the exact bug this replaced.
      naturalW = img.naturalWidth; naturalH = img.naturalHeight;
      setBusy(false);
      fitStage(); paint();
    };
  });
}
